import React, { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../../components/Layout';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    const msg = Array.isArray(err.message) ? err.message.join('; ') : err.message;
    const e = new Error(msg || 'Request failed') as Error & { status?: number };
    e.status = res.status;
    throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

// PDFs are behind the separation guard, so open them by fetching with the token
// and opening the blob (a plain link would have no auth header).
async function openSeparationPdf(id: string, kind: 'record' | 'instructions') {
  const token = localStorage.getItem('admin_auth_token');
  const win = window.open('', '_blank');
  try {
    const res = await fetch(`${API_BASE}/separations/${id}/${kind}.pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Couldn't open the ${kind} (${res.status})`);
    const url = URL.createObjectURL(await res.blob());
    if (win) win.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e: any) {
    win?.close();
    alert(e.message);
  }
}

async function uploadSeparationScan(id: string, file: File, label: string) {
  const token = localStorage.getItem('admin_auth_token');
  const body = new FormData();
  body.append('file', file);
  if (label) body.append('label', label);
  const res = await fetch(`${API_BASE}/separations/${id}/attachments`, {
    method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(Array.isArray(err.message) ? err.message.join('; ') : err.message || 'Upload failed');
  }
  return res.json();
}

async function openSeparationAttachment(id: string, attachmentId: string) {
  const token = localStorage.getItem('admin_auth_token');
  const win = window.open('', '_blank');
  try {
    const res = await fetch(`${API_BASE}/separations/${id}/attachments/${attachmentId}/file`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Couldn't open the attachment (${res.status})`);
    const url = URL.createObjectURL(await res.blob());
    if (win) win.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e: any) { win?.close(); alert(e.message); }
}

const TYPE_OPTIONS = [
  { value: 'involuntary', label: 'Involuntary separation' },
  { value: 'voluntary', label: 'Voluntary resignation' },
  { value: 'job_abandonment', label: 'Job abandonment' },
  { value: 'end_of_temp', label: 'End of temporary assignment' },
  { value: 'mutual', label: 'Mutual separation' },
];
const typeLabel = (v: string) => TYPE_OPTIONS.find(t => t.value === v)?.label || v || '—';
const REHIRE = [{ value: 'yes', label: 'Eligible' }, { value: 'no', label: 'Not eligible' }, { value: 'case_by_case', label: 'Case by case' }];
const INFORMED = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'did_not_ask', label: 'Did not ask' }];
const fmtDate = (d: any) => (d ? new Date(String(d).length <= 10 ? `${String(d).slice(0, 10)}T00:00:00` : d).toLocaleDateString() : '—');

function Modal({ title, onClose, children, size = 'lg' }: { title: string; onClose: () => void; children: React.ReactNode; size?: 'md' | 'lg' | 'xl' }) {
  const w = { md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-5xl' }[size];
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-start justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className={`relative bg-white rounded-xl shadow-2xl ${w} w-full my-8`}>
          <div className="border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'withdrawn') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-300">Withdrawn</span>;
  const finalized = status === 'finalized';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${finalized ? 'bg-gray-800 text-white' : 'bg-amber-100 text-amber-800 border border-amber-300'}`}>{finalized ? 'Finalized' : 'Draft'}</span>;
}

export function SeparationsPage() {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showWithdrawn, setShowWithdrawn] = useState(false);

  const load = async () => {
    setLoading(true); setLoadError(''); setForbidden(false);
    try {
      const [list, emps] = await Promise.all([
        apiRequest(`/separations${showWithdrawn ? '?includeWithdrawn=true' : ''}`),
        apiRequest('/employees?status=active').catch(() => []),
      ]);
      setRows(list || []);
      setEmployees((emps || []).map((e: any) => ({ id: e.id, name: e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim() })));
    } catch (e: any) {
      if (e.status === 403) setForbidden(true); else setLoadError(e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showWithdrawn]);

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  if (forbidden) return (
    <>
      <PageHeader title="Employee Separations" subtitle="Open, finalize and print a separation record" />
      <Card><div className="text-center py-10">
        <div className="text-lg font-semibold text-gray-900">You don't have Separation access</div>
        <div className="text-sm text-gray-600 mt-2 max-w-md mx-auto">Separations are limited to employees granted Separation Access in User Management — a tighter grant than Discipline.</div>
      </div></Card>
    </>
  );

  return (
    <>
      <PageHeader
        title="Employee Separations"
        subtitle="A separation is its own record — never a warning. Draft it, finalize it, print the packet."
        actions={
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 select-none">
              <input type="checkbox" checked={showWithdrawn} onChange={e => setShowWithdrawn(e.target.checked)} className="accent-gray-700" />
              Show withdrawn
            </label>
            <button onClick={() => setShowNew(true)} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">New separation</button>
          </div>
        }
      />
      {loadError && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{loadError}</div>}
      <Card>
        {rows.length === 0 ? (
          <p className="text-gray-500 text-center py-10">No separations yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b"><tr className="text-left text-sm text-gray-600">
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Separation date</th>
                <th className="px-4 py-3 font-medium">Opened</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y">
                {rows.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{s.employeeName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{typeLabel(s.separationType)}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s.separationDate ? fmtDate(s.separationDate) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(s.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setOpenId(s.id)} className="text-blue-600 hover:text-blue-700 font-medium text-sm">Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showNew && <NewSeparationModal employees={employees} onClose={() => setShowNew(false)} onDone={(id) => { setShowNew(false); load(); setOpenId(id); }} />}
      {openId && <SeparationDetailModal id={openId} employees={employees} onClose={() => { setOpenId(null); load(); }} onChanged={load} />}
    </>
  );
}

function NewSeparationModal({ employees, onClose, onDone }: { employees: any[]; onClose: () => void; onDone: (id: string) => void }) {
  const [employeeId, setEmployeeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const create = async () => {
    if (!employeeId) { setError('Select an employee.'); return; }
    setSaving(true); setError('');
    try {
      const sep = await apiRequest('/separations', { method: 'POST', body: JSON.stringify({ employeeId }) });
      onDone(sep.id);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };
  return (
    <Modal title="New separation" onClose={onClose} size="md">
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>}
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-lg text-sm">
          This opens a <strong>draft only</strong>. Nothing is issued and nothing changes for the employee until you fill it in and finalize it.
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
          <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
            <option value="">Select employee…</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={create} disabled={saving} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50">{saving ? 'Opening…' : 'Open draft'}</button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><div className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">{label}</div><div className="text-sm text-gray-900">{children}</div></div>;
}

function SeparationDetailModal({ id, employees, onClose, onChanged }: { id: string; employees: any[]; onClose: () => void; onChanged: () => void }) {
  const [sep, setSep] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [accessLog, setAccessLog] = useState<any[] | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [scanFile, setScanFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');

  // draft form
  const [form, setForm] = useState<any>({});
  // post-meeting form
  const [pm, setPm] = useState<any>({});
  // amendment form
  const [amend, setAmend] = useState({ fieldLabel: '', newValue: '', amendmentNote: '' });

  const reload = async () => {
    try {
      const s = await apiRequest(`/separations/${id}`);
      setSep(s);
      setForm({ separationType: s.separationType || '', reason: s.reason || '', authorizedBy: s.authorizedBy || '', rehireEligibility: s.rehireEligibility || '', narrative: s.narrative || '' });
    } catch (e: any) { setError(e.message); }
  };
  const loadAttachments = async () => { try { setAttachments((await apiRequest(`/separations/${id}/attachments`)) || []); } catch { /* leave as-is */ } };
  useEffect(() => { reload(); loadAttachments(); }, [id]);

  const upload = async () => {
    if (!scanFile) return;
    setUploading(true); setError('');
    try { await uploadSeparationScan(id, scanFile, 'Signed separation record'); setScanFile(null); await loadAttachments(); }
    catch (e: any) { setError(e.message); } finally { setUploading(false); }
  };

  if (!sep) return <Modal title="Separation" onClose={onClose}><div className="py-10 flex justify-center"><LoadingSpinner /></div></Modal>;
  const withdrawn = sep.status === 'withdrawn';
  const draft = sep.status === 'draft';
  const empName = (eid: string) => employees.find(e => e.id === eid)?.name || eid || '—';

  const saveDraft = async () => {
    setBusy('draft'); setError('');
    try { await apiRequest(`/separations/${id}`, { method: 'PATCH', body: JSON.stringify(form) }); await reload(); onChanged(); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };
  const finalize = async () => {
    if (!window.confirm('Finalize this separation? Once finalized it can NOT be edited — the facts are frozen and corrections are made only by amendment.')) return;
    setBusy('finalize'); setError('');
    try { await apiRequest(`/separations/${id}/finalize`, { method: 'POST' }); await reload(); onChanged(); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };
  const withdraw = async () => {
    if (!withdrawReason.trim()) { setError('A withdrawal needs a written reason.'); return; }
    setBusy('withdraw'); setError('');
    try { await apiRequest(`/separations/${id}/withdraw`, { method: 'POST', body: JSON.stringify({ reason: withdrawReason.trim() }) }); setShowWithdraw(false); setWithdrawReason(''); await reload(); onChanged(); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };
  const savePostMeeting = async () => {
    const body: any = {};
    Object.entries(pm).forEach(([k, v]) => { if (v !== '' && v !== undefined && v !== null) body[k] = k === 'finalPayAmount' ? Number(v) : v; });
    if (Object.keys(body).length === 0) { setError('Nothing to save.'); return; }
    setBusy('pm'); setError('');
    try { await apiRequest(`/separations/${id}/post-meeting`, { method: 'PATCH', body: JSON.stringify(body) }); setPm({}); await reload(); onChanged(); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };
  const submitAmend = async () => {
    if (!amend.fieldLabel.trim() || !amend.newValue.trim() || !amend.amendmentNote.trim()) { setError('An amendment needs the field, the new value, and a reason.'); return; }
    setBusy('amend'); setError('');
    try { await apiRequest(`/separations/${id}/amend`, { method: 'POST', body: JSON.stringify(amend) }); setAmend({ fieldLabel: '', newValue: '', amendmentNote: '' }); await reload(); onChanged(); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };
  const loadAccessLog = async () => { try { setAccessLog(await apiRequest(`/separations/${id}/access-log`)); } catch (e: any) { setError(e.message); } };

  const dl = sep.deadlines || {};
  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';
  const empSelect = (val: string, on: (v: string) => void, allowNone = true) => (
    <select value={val || ''} onChange={e => on(e.target.value)} className={input}>
      {allowNone && <option value="">—</option>}
      {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
    </select>
  );

  return (
    <Modal title={`Separation — ${sep.employeeName || ''}`} onClose={onClose} size="xl">
      <div className="space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={sep.status} />
          <span className="text-sm text-gray-500">Opened {fmtDate(sep.createdAt)}</span>
          {sep.finalizedAt && <span className="text-sm text-gray-500">· Finalized {fmtDate(sep.finalizedAt)}</span>}
          {!withdrawn && (
            <div className="ml-auto flex gap-2">
              <button onClick={() => openSeparationPdf(id, 'record')} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Print Separation Record</button>
              <button onClick={() => openSeparationPdf(id, 'instructions')} className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Print Manager Instructions</button>
            </div>
          )}
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>}

        {withdrawn ? (
          <div className="space-y-4">
            <div className="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-3 rounded-lg text-sm">
              <div className="font-semibold text-gray-800">This draft was withdrawn</div>
              <div className="mt-1">Withdrawn {fmtDate(sep.withdrawnAt)}{sep.withdrawnByName ? ` by ${sep.withdrawnByName}` : ''}. It stays on file — never deleted — and can no longer be finalized, edited, or printed.</div>
            </div>
            <Field label="Reason for withdrawal"><div className="whitespace-pre-wrap">{sep.withdrawalReason || '—'}</div></Field>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Type">{typeLabel(sep.separationType)}</Field>
              <Field label="Opened">{fmtDate(sep.createdAt)}</Field>
            </div>
            {sep.reason && <Field label="Draft reason (as written)"><div className="whitespace-pre-wrap">{sep.reason}</div></Field>}
            <div className="border-t pt-4">
              {accessLog === null ? (
                <button onClick={loadAccessLog} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Show who has read this record</button>
              ) : (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Read log</div>
                  <ul className="text-sm text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {accessLog.map((r: any, i: number) => <li key={i}>{r.accessed_by_name || 'Unknown'} — {new Date(r.accessed_at).toLocaleString()}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ) : draft ? (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-lg text-sm">Draft — nothing is frozen yet. The reason is written by you; the system never generates it. Finalizing freezes the facts and cannot be undone.</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.separationType} onChange={e => setForm({ ...form, separationType: e.target.value })} className={input}>
                  <option value="">Select…</option>
                  {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rehire eligibility</label>
                <select value={form.rehireEligibility} onChange={e => setForm({ ...form, rehireEligibility: e.target.value })} className={input}>
                  <option value="">Select…</option>
                  {REHIRE.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Authorized by</label>
                {empSelect(form.authorizedBy, v => setForm({ ...form, authorizedBy: v }))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-gray-400 font-normal">— you write it; this is the sentence that prints on the record</span></label>
              <textarea rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className={input} placeholder="e.g. Not meeting the expectations of the position." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional context <span className="text-gray-400 font-normal">(optional, internal)</span></label>
              <textarea rows={2} value={form.narrative} onChange={e => setForm({ ...form, narrative: e.target.value })} className={input} />
            </div>
            {showWithdraw && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                <label className="block text-sm font-medium text-gray-700">Reason for withdrawing this draft <span className="text-gray-400 font-normal">— required; e.g. "Opened for the wrong employee"</span></label>
                <textarea rows={2} value={withdrawReason} onChange={e => setWithdrawReason(e.target.value)} className={input} placeholder="Why is this draft being withdrawn?" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setShowWithdraw(false); setWithdrawReason(''); }} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                  <button onClick={withdraw} disabled={!!busy} className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">{busy === 'withdraw' ? 'Withdrawing…' : 'Confirm withdraw'}</button>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 pt-2 border-t">
              <button onClick={() => setShowWithdraw(v => !v)} className="text-sm text-gray-500 hover:text-gray-700">Withdraw draft</button>
              <div className="flex gap-3">
                <button onClick={saveDraft} disabled={!!busy} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50">{busy === 'draft' ? 'Saving…' : 'Save draft'}</button>
                <button onClick={finalize} disabled={!!busy} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50">{busy === 'finalize' ? 'Finalizing…' : 'Finalize'}</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Type">{typeLabel(sep.separationType)}</Field>
              <Field label="Rehire">{REHIRE.find(r => r.value === sep.rehireEligibility)?.label || '—'}</Field>
              <Field label="Authorized by">{empName(sep.authorizedBy)}</Field>
              <Field label="Separation date">{sep.separationDate ? fmtDate(sep.separationDate) : <span className="text-amber-600">not set</span>}</Field>
            </div>
            <Field label="Reason"><div className="whitespace-pre-wrap">{sep.reason}</div></Field>

            <div className="bg-gray-50 border rounded-lg p-4">
              <div className="text-sm font-semibold text-gray-700 mb-2">Deadlines</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Final pay:</span> {dl.finalPay?.dueLabel ? <span className={dl.finalPay.overdue ? 'text-red-600 font-semibold' : ''}>{dl.finalPay.dueLabel}{dl.finalPay.overdue ? ' — OVERDUE' : ''}</span> : (dl.finalPay?.note || '—')}</div>
                <div><span className="text-gray-500">TWC response:</span> {dl.twc?.dueLabel ? <span className={dl.twc.overdue ? 'text-red-600 font-semibold' : ''}>{dl.twc.dueLabel}{dl.twc.overdue ? ' — OVERDUE' : ''}</span> : (dl.twc?.note || '—')}</div>
              </div>
            </div>

            {/* Post-meeting fill-once fields */}
            <div>
              <div className="text-sm font-semibold text-gray-700 mb-2">Record after the meeting <span className="text-gray-400 font-normal">— each fills once; a set value is changed only by amendment</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {!sep.separationDate && <div><label className="block text-xs text-gray-500 mb-1">Separation date</label><input type="date" className={input} value={pm.separationDate || ''} onChange={e => setPm({ ...pm, separationDate: e.target.value })} /></div>}
                <div><label className="block text-xs text-gray-500 mb-1">Effective time</label><input className={input} placeholder="e.g. 2:00 PM" value={pm.effectiveTime ?? (sep.effectiveTime || '')} disabled={!!sep.effectiveTime} onChange={e => setPm({ ...pm, effectiveTime: e.target.value })} /></div>
                <div><label className="block text-xs text-gray-500 mb-1">Conducted by</label>{sep.conductedBy ? <div className="text-sm py-2">{empName(sep.conductedBy)}</div> : empSelect(pm.conductedBy || '', v => setPm({ ...pm, conductedBy: v }))}</div>
                <div><label className="block text-xs text-gray-500 mb-1">Witness</label><input className={input} value={pm.witnessName ?? (sep.witnessName || '')} disabled={!!sep.witnessName} onChange={e => setPm({ ...pm, witnessName: e.target.value })} /></div>
                <div><label className="block text-xs text-gray-500 mb-1">Employee informed of reason</label><select className={input} value={pm.employeeInformedOfReason ?? (sep.employeeInformedOfReason || '')} disabled={!!sep.employeeInformedOfReason} onChange={e => setPm({ ...pm, employeeInformedOfReason: e.target.value })}><option value="">—</option>{INFORMED.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}</select></div>
                <div><label className="block text-xs text-gray-500 mb-1">Final pay amount</label><input type="number" className={input} value={pm.finalPayAmount ?? (sep.finalPayAmount ?? '')} disabled={sep.finalPayAmount != null} onChange={e => setPm({ ...pm, finalPayAmount: e.target.value })} /></div>
                <div><label className="block text-xs text-gray-500 mb-1">Final pay method</label><input className={input} value={pm.finalPayMethod ?? (sep.finalPayMethod || '')} disabled={!!sep.finalPayMethod} onChange={e => setPm({ ...pm, finalPayMethod: e.target.value })} /></div>
                <div><label className="block text-xs text-gray-500 mb-1">Final pay date issued</label><input type="date" className={input} value={pm.finalPayDateIssued ?? (sep.finalPayDateIssued ? String(sep.finalPayDateIssued).slice(0,10) : '')} disabled={!!sep.finalPayDateIssued} onChange={e => setPm({ ...pm, finalPayDateIssued: e.target.value })} /></div>
                <div><label className="block text-xs text-gray-500 mb-1">TWC notice mailed date</label><input type="date" className={input} value={pm.twcNoticeMailedDate ?? (sep.twcNoticeMailedDate ? String(sep.twcNoticeMailedDate).slice(0,10) : '')} disabled={!!sep.twcNoticeMailedDate} onChange={e => setPm({ ...pm, twcNoticeMailedDate: e.target.value })} /></div>
              </div>
              <div className="flex justify-end mt-3"><button onClick={savePostMeeting} disabled={!!busy} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">{busy === 'pm' ? 'Saving…' : 'Save details'}</button></div>
            </div>

            {/* Amendments */}
            <div className="border-t pt-4">
              <div className="text-sm font-semibold text-gray-700 mb-2">Amendments</div>
              {(sep.amendments || []).length === 0 ? <p className="text-sm text-gray-400">None.</p> : (
                <ul className="space-y-2 mb-3">
                  {sep.amendments.map((a: any) => (
                    <li key={a.id} className="text-sm bg-gray-50 border rounded px-3 py-2">
                      <span className="font-medium">{a.fieldLabel}:</span> {a.previousValue ? <span className="line-through text-gray-400">{a.previousValue}</span> : ''} → {a.newValue}
                      <div className="text-xs text-gray-500 mt-0.5">{a.amendmentNote} — {a.authorName || ''} {fmtDate(a.createdAt)}</div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input className={input} placeholder="Field (e.g. reason)" value={amend.fieldLabel} onChange={e => setAmend({ ...amend, fieldLabel: e.target.value })} />
                <input className={input} placeholder="New value" value={amend.newValue} onChange={e => setAmend({ ...amend, newValue: e.target.value })} />
                <input className={input} placeholder="Reason for the change" value={amend.amendmentNote} onChange={e => setAmend({ ...amend, amendmentNote: e.target.value })} />
              </div>
              <div className="flex justify-end mt-2"><button onClick={submitAmend} disabled={!!busy} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm">{busy === 'amend' ? 'Saving…' : 'Add amendment'}</button></div>
            </div>

            {/* Access log */}
            <div className="border-t pt-4">
              {accessLog === null ? (
                <button onClick={loadAccessLog} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Show who has read this record</button>
              ) : (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Read log</div>
                  <ul className="text-sm text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {accessLog.map((r: any, i: number) => <li key={i}>{r.accessed_by_name || 'Unknown'} — {new Date(r.accessed_at).toLocaleString()}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Signed scan on file — attach the signed record back into the system */}
        {!withdrawn && (
        <div className="border-t pt-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Signed scan on file</div>
          {attachments.length === 0 ? (
            <p className="text-sm text-gray-400 mb-3">Nothing attached yet.</p>
          ) : (
            <ul className="space-y-2 mb-3">
              {attachments.map(a => (
                <li key={a.id} className="flex items-center justify-between gap-3 text-sm bg-gray-50 border rounded px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{a.originalFilename}</div>
                    <div className="text-xs text-gray-500">{a.label}{a.uploadedByName ? ` · ${a.uploadedByName}` : ''} · {fmtDate(a.createdAt)}</div>
                  </div>
                  <button onClick={() => openSeparationAttachment(id, a.id)} className="px-3 py-1 text-blue-600 border border-blue-200 rounded hover:bg-blue-50 shrink-0">Open</button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={e => setScanFile(e.target.files?.[0] || null)}
              className="block text-sm text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-gray-300 file:bg-white file:text-gray-700 hover:file:bg-gray-50" />
            <button onClick={upload} disabled={!scanFile || uploading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">{uploading ? 'Uploading…' : 'Attach scan'}</button>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Print the record, sign it, scan it (PDF or photo), and attach it here. Stored securely; reachable only by someone with Separation access.</p>
        </div>
        )}
      </div>
    </Modal>
  );
}

export default SeparationsPage;
